// Pure helpers for the attention-oriented invoice/bill worklists. Kept out
// of the page components so the due-label and balance rules can be unit
// tested without a DOM.

import type { Bill, Invoice } from "@/lib/api";

type DueTone = "success" | "warning" | "danger" | "neutral";

interface DueLabel {
  /** Human label: "Due today", "Due in 5d", "12d overdue". */
  label: string;
  /** Escalating emphasis for badges and rows. */
  tone: DueTone;
  /** Whole days overdue (positive = late, 0 = due today, negative = future). */
  daysOverdue: number;
}

const MS_PER_DAY = 86_400_000;

/** Whole days from `dueAt` to `now` in UTC calendar days, or null when the
 * date cannot be parsed. */
export function daysBetween(dueAt: string, now: Date): number | null {
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return null;
  const dueDay = Date.UTC(
    due.getUTCFullYear(),
    due.getUTCMonth(),
    due.getUTCDate(),
  );
  const nowDay = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  return Math.round((nowDay - dueDay) / MS_PER_DAY);
}

/** Prefer the server's `days_overdue`; otherwise derive it from `due_at`. */
function resolveDaysOverdue(
  dueAt: string | undefined,
  provided?: number,
  now: Date = new Date(),
): number {
  if (typeof provided === "number" && Number.isFinite(provided)) return provided;
  if (!dueAt) return 0;
  return daysBetween(dueAt, now) ?? 0;
}

/** Derived timing label with escalating emphasis. A missing/blank due date
 * returns a neutral "No due date". */
export function dueLabel(
  dueAt: string | undefined,
  provided?: number,
  now: Date = new Date(),
): DueLabel {
  if (!dueAt && !(typeof provided === "number" && Number.isFinite(provided))) {
    return { label: "No due date", tone: "neutral", daysOverdue: 0 };
  }
  const daysOverdue = resolveDaysOverdue(dueAt, provided, now);
  if (daysOverdue > 0) {
    return {
      label: `${daysOverdue}d overdue`,
      tone: daysOverdue > 30 ? "danger" : "warning",
      daysOverdue,
    };
  }
  if (daysOverdue === 0) {
    return { label: "Due today", tone: "warning", daysOverdue: 0 };
  }
  const days = Math.abs(daysOverdue);
  return {
    label: `Due in ${days}d`,
    tone: days <= 7 ? "warning" : "neutral",
    daysOverdue,
  };
}

/** Derived timing label from a record carrying the frozen enriched fields. */
export function recordDueLabel(
  record: Pick<Invoice, "due_at" | "days_overdue">,
  now: Date = new Date(),
): DueLabel {
  return dueLabel(record.due_at, record.days_overdue, now);
}

interface PartPaidShape {
  amount_paid_cents?: number;
  balance_due_cents?: number;
}

/** True when a document has both a payment applied and a remaining balance. */
export function isPartPaid(document: PartPaidShape): boolean {
  return (
    (document.amount_paid_cents ?? 0) > 0 &&
    (document.balance_due_cents ?? 0) > 0
  );
}

export function payableBalanceCents(
  record: Pick<Invoice, "balance_due_cents" | "total_minor">,
): number {
  return record.balance_due_cents ?? record.total_minor;
}

export function billBalanceCents(
  record: Pick<Bill, "balance_due_cents" | "total_cents">,
): number {
  return record.balance_due_cents ?? record.total_cents;
}

const OPEN_INVOICE_STATUSES = new Set(["sent", "overdue"]);

/** A sent/overdue invoice with money still owed can receive AR relief. */
export function canCreditInvoice(invoice: Invoice): boolean {
  return (
    OPEN_INVOICE_STATUSES.has(invoice.status.toLowerCase()) &&
    payableBalanceCents(invoice) > 0
  );
}

/** A sent/overdue invoice with money still owed can be reminded. */
export function canRemindInvoice(invoice: Invoice): boolean {
  return (
    OPEN_INVOICE_STATUSES.has(invoice.status.toLowerCase()) &&
    payableBalanceCents(invoice) > 0
  );
}

/** Only a draft can be moved to sent. */
export function canMarkInvoiceSent(invoice: Invoice): boolean {
  return invoice.status.toLowerCase() === "draft";
}

export function isSelectableInvoice(invoice: Invoice): boolean {
  return canRemindInvoice(invoice) || canMarkInvoiceSent(invoice);
}

/** Open invoice whose due date has passed, including a stored overdue status. */
export function invoiceIsOverdue(
  invoice: Pick<Invoice, "status" | "due_at" | "days_overdue">,
): boolean {
  const status = invoice.status.toLowerCase();
  if (status === "paid" || status === "draft" || status === "void") return false;
  if (status === "overdue") return true;
  return recordDueLabel(invoice).daysOverdue > 0;
}

/** Open bill whose due date has passed, including a stored overdue status. */
export function billIsOverdue(
  bill: Pick<Bill, "status" | "due_at" | "days_overdue">,
): boolean {
  const status = bill.status.toLowerCase();
  if (status === "paid" || status === "draft" || status === "void") return false;
  if (status === "overdue") return true;
  return recordDueLabel(bill).daysOverdue > 0;
}

/** Stable bill-list filter. "received" is awaiting payment and excludes overdue rows. */
export function matchesBillListFilter(bill: Bill, filter: string): boolean {
  const status = bill.status.toLowerCase();
  if (!filter) return true;
  if (filter === "overdue") return billIsOverdue(bill);
  if (filter === "received") return status === "received" && !billIsOverdue(bill);
  return status === filter;
}

/** One list/page status label. Overdue wins once the due date has passed. */
export function invoiceListStatus(invoice: Invoice): string {
  const status = invoice.status.toLowerCase();
  if (status === "void") return "Void";
  if (status === "paid") return "Paid";
  if (status === "draft") return "Draft";
  if (invoiceIsOverdue(invoice)) return "Overdue";
  if (isPartPaid(invoice)) return "Part paid";
  return "Awaiting payment";
}

/** Stable invoice-list filter. "sent" is awaiting payment and excludes overdue rows. */
export function matchesInvoiceListFilter(invoice: Invoice, filter: string): boolean {
  const status = invoice.status.toLowerCase();
  if (!filter) return true;
  if (filter === "overdue") return invoiceIsOverdue(invoice);
  if (filter === "sent") return status === "sent" && !invoiceIsOverdue(invoice);
  return status === filter;
}

/** Not-yet-overdue (or due today) and inside the look-ahead window. */
export function isDueWithinDays(
  dueAt: string | undefined,
  days: number,
  now: Date = new Date(),
): boolean {
  const daysOverdue = resolveDaysOverdue(dueAt, undefined, now);
  return daysOverdue <= 0 && daysOverdue >= -days;
}

/** Client-side "due within N days with a balance" attention filter. */
export function billNeedsAttention(bill: Bill, days = 7, now: Date = new Date()) {
  return (
    billBalanceCents(bill) > 0 &&
    bill.status.toLowerCase() !== "paid" &&
    isDueWithinDays(bill.due_at, days, now)
  );
}
